import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Award, Building2, Star, Check, BarChart2 } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Badge, Modal, TextArea, toast } from '../../components/common';
import { rfqApi } from '../../services/api';
import './RFQAward.css';

export default function RFQAward() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [notes, setNotes] = useState('');

    const { data: rfq, isLoading } = useQuery({
        queryKey: ['rfq', id],
        queryFn: () => rfqApi.get(id),
        select: (res) => res.data?.data
    });

    const awardMutation = useMutation({
        mutationFn: (quotationId) => rfqApi.award(id, { quotationId, notes }),
        onSuccess: () => {
            toast.success('Pemenang pengadaan berhasil ditetapkan!');
            queryClient.invalidateQueries(['rfq', id]);
            queryClient.invalidateQueries(['rfqs']);
            navigate(`/rfq/${id}`);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Gagal menetapkan pemenang');
        }
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    if (isLoading) {
        return <div className="loading-state">Memuat data penawaran...</div>;
    }

    const quotations = rfq?.quotations?.filter(q => ['submitted', 'under_evaluation', 'evaluated'].includes(q.status)) || [];

    // Calculate totals & scores
    const quotationsWithTotals = quotations.map(q => {
        const lineTotal = (q.lines || q.items)?.reduce((sum, item) => sum + (parseFloat(item.quantity || 1) * parseFloat(item.unitPrice || 0)), 0);
        const total = parseFloat(q.totalAmount || 0) || lineTotal || 0;
        return {
            ...q,
            total,
            overallScore: parseFloat(q.overallScore || 0)
        };
    }).sort((a, b) => {
        if (b.overallScore !== a.overallScore) return b.overallScore - a.overallScore;
        return a.total - b.total;
    });

    const lowestTotal = quotationsWithTotals.reduce((min, q) => q.total > 0 && q.total < min ? q.total : min, Infinity);

    const handleAward = () => {
        if (!selectedQuotation) {
            toast.error('Pilih salah satu penawaran vendor untuk ditetapkan sebagai pemenang');
            return;
        }
        setShowConfirm(true);
    };

    const confirmAward = () => {
        awardMutation.mutate(selectedQuotation.id);
    };

    return (
        <div className="rfq-award-page">
            <PageHeader
                title="Penetapan Pemenang Vendor (Award)"
                subtitle={`Pilih penawaran terbaik berdasarkan hasil scoring & komersial untuk ${rfq?.rfqNumber}`}
                breadcrumbs={[
                    { label: 'RFQ', path: '/rfq' },
                    { label: rfq?.rfqNumber, path: `/rfq/${id}` },
                    { label: 'Penetapan Pemenang' }
                ]}
            />

            <Card>
                <CardBody>
                    <div className="award-intro">
                        <h3>Pilih Vendor Pemenang Pengadaan</h3>
                        <p>Daftar penawaran diurutkan berdasarkan Peringkat Hasil Scoring &amp; Efisiensi Harga.</p>
                    </div>

                    <div className="quotations-list">
                        {quotationsWithTotals.map((q, index) => {
                            const vendorName = q.vendor?.companyName || q.vendor?.name || 'Vendor ' + (index + 1);
                            const vendorEmail = q.vendor?.email || q.vendor?.vendorCode || '-';
                            const isRank1 = index === 0;

                            return (
                                <div
                                    key={q.id}
                                    className={`quotation-card ${selectedQuotation?.id === q.id ? 'selected' : ''} ${isRank1 ? 'lowest' : ''}`}
                                    onClick={() => setSelectedQuotation(q)}
                                >
                                    <div className="quotation-rank">
                                        {isRank1 ? (
                                            <div className="rank-badge gold">
                                                <Star size={16} fill="#fbbf24" />
                                                <span>Rekomendasi #1</span>
                                            </div>
                                        ) : (
                                            <div className="rank-number">#{index + 1}</div>
                                        )}
                                    </div>

                                    <div className="quotation-vendor">
                                        <div className="vendor-icon">
                                            <Building2 size={20} />
                                        </div>
                                        <div className="vendor-info">
                                            <h4>{vendorName}</h4>
                                            <p>{vendorEmail} • {q.quotationNumber}</p>
                                        </div>
                                    </div>

                                    {/* Overall Score indicator */}
                                    <div className="quotation-score px-4 text-center">
                                        <span className="text-[11px] text-slate-500 font-semibold uppercase block">Skor Evaluasi</span>
                                        <span className="text-lg font-extrabold text-blue-700">
                                            {q.overallScore > 0 ? q.overallScore.toFixed(1) : '—'}
                                            <span className="text-xs text-slate-400 font-normal"> / 100</span>
                                        </span>
                                    </div>

                                    <div className="quotation-price">
                                        <span className="price-label">Total Penawaran</span>
                                        <span className="price-value">{formatCurrency(q.total)}</span>
                                        {index > 0 && lowestTotal !== Infinity && (
                                            <span className="price-diff">
                                                +{formatCurrency(q.total - lowestTotal)}
                                            </span>
                                        )}
                                    </div>

                                    <div className="quotation-select">
                                        {selectedQuotation?.id === q.id ? (
                                            <Check size={24} className="text-white" />
                                        ) : (
                                            <div className="select-circle" />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="award-actions">
                        <Button variant="secondary" onClick={() => navigate(`/rfq/${id}`)}>
                            Kembali ke Detail RFQ
                        </Button>
                        <Button
                            icon={Award}
                            onClick={handleAward}
                            disabled={!selectedQuotation}
                        >
                            Tetapkan Vendor Terpilih Sebagai Pemenang
                        </Button>
                    </div>
                </CardBody>
            </Card>

            <Modal
                isOpen={showConfirm}
                onClose={() => setShowConfirm(false)}
                title="Konfirmasi Penetapan Pemenang"
                size="sm"
            >
                <div className="award-confirm">
                    <p className="text-sm text-slate-700 mb-4">
                        Anda akan menetapkan pemenang pengadaan ini kepada{' '}
                        <strong>{selectedQuotation?.vendor?.companyName || selectedQuotation?.vendor?.name}</strong>{' '}
                        dengan total penawaran <strong>{formatCurrency(selectedQuotation?.total)}</strong>{' '}
                        dan Skor Evaluasi <strong>{selectedQuotation?.overallScore?.toFixed(1) || '—'} / 100</strong>.
                    </p>

                    <TextArea
                        label="Catatan Keputusan Penetapan (Opsional)"
                        placeholder="Tambahkan justifikasi penetapan pemenang tender..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                    />

                    <div className="confirm-actions flex justify-end gap-2 mt-4">
                        <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                            Batal
                        </Button>
                        <Button icon={Award} onClick={confirmAward} loading={awardMutation.isPending}>
                            Konfirmasi &amp; Terbitkan Penetapan
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
